import { Injectable, signal, computed } from '@angular/core';
import { Toast, ToastConfig, ToastPosition } from './toast.types';

const DEFAULT_DURATION = 5000;
const DEFAULT_POSITION: ToastPosition = 'top-right';

/**
 * Service for managing toast notifications.
 *
 * @example
 * ```typescript
 * private readonly toast = inject(ToastService);
 *
 * onSave() {
 *   this.toast.success('Changes saved successfully', 'Success');
 * }
 *
 * onError() {
 *   this.toast.error('Failed to save changes');
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);
  private readonly _position = signal<ToastPosition>(DEFAULT_POSITION);
  private readonly _defaultDuration = signal<number>(DEFAULT_DURATION);
  private readonly _timeoutMap = new Map<string, ReturnType<typeof setTimeout>>();

  /** Readonly signal of active toasts */
  readonly toasts = computed(() => this._toasts());

  /** Readonly signal of current toast position */
  readonly position = computed(() => this._position());

  /**
   * Show a success toast notification.
   * @param message The message to display
   * @param title Optional title
   * @returns The toast id
   */
  success(message: string, title?: string): string {
    return this.show({ message, title, status: 'success' });
  }

  /**
   * Show an error toast notification.
   * @param message The message to display
   * @param title Optional title
   * @returns The toast id
   */
  error(message: string, title?: string): string {
    return this.show({ message, title, status: 'error' });
  }

  /**
   * Show a warning toast notification.
   * @param message The message to display
   * @param title Optional title
   * @returns The toast id
   */
  warning(message: string, title?: string): string {
    return this.show({ message, title, status: 'warning' });
  }

  /**
   * Show an info toast notification.
   * @param message The message to display
   * @param title Optional title
   * @returns The toast id
   */
  info(message: string, title?: string): string {
    return this.show({ message, title, status: 'info' });
  }

  /**
   * Show a toast notification with full configuration.
   * @param config The toast configuration
   * @returns The toast id
   */
  show(config: ToastConfig): string {
    const id = this.generateId();
    const toast: Toast = {
      ...config,
      id,
      createdAt: Date.now(),
      duration: config.duration ?? this._defaultDuration(),
      dismissible: config.dismissible ?? true,
    };

    this._toasts.update(toasts => [...toasts, toast]);

    if (toast.duration > 0) {
      this.scheduleAutoDismiss(toast);
    }

    return id;
  }

  /**
   * Dismiss a specific toast by id.
   * @param id The toast id to dismiss
   */
  dismiss(id: string): void {
    this.clearTimeout(id);
    this._toasts.update(toasts => toasts.filter(t => t.id !== id));
  }

  /**
   * Update an existing toast in-place.
   * If the toast does not exist, this is a no-op.
   */
  update(id: string, patch: Partial<Omit<Toast, 'id' | 'createdAt'>>): void {
    this._toasts.update(toasts => toasts.map(toast => (toast.id === id ? { ...toast, ...patch } : toast)));
  }

  /**
   * Dismiss all active toasts.
   */
  dismissAll(): void {
    this._toasts().forEach(toast => this.clearTimeout(toast.id));
    this._toasts.set([]);
  }

  /**
   * Set the position for displaying toasts.
   * @param position The new position
   */
  setPosition(position: ToastPosition): void {
    this._position.set(position);
  }

  /**
   * Set the default duration for auto-dismiss.
   * @param duration Duration in milliseconds. 0 = no auto-dismiss.
   */
  setDefaultDuration(duration: number): void {
    this._defaultDuration.set(duration);
  }

  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private scheduleAutoDismiss(toast: Toast): void {
    const timeout = setTimeout(() => {
      this.dismiss(toast.id);
    }, toast.duration);

    this._timeoutMap.set(toast.id, timeout);
  }

  private clearTimeout(id: string): void {
    const timeout = this._timeoutMap.get(id);
    if (timeout) {
      clearTimeout(timeout);
      this._timeoutMap.delete(id);
    }
  }
}
